<template>
  <div class="container v-content-container">
    <div class="row">
      <div class="col-xs-12">
        <h1>
          <i class="fa fa-fw fa-lg fa-cubes"> </i> Event Bookings
          <router-link
            :to="{ name: 'NewBookingEvent' }"
            id="button-new-booking"
            class="btn btn-v-lightgreen-white pull-right"
            :title="$label('booking.create_new_booking', 'Create new booking')"
          >
            <i class="fa fa-lg fa-plus"></i>
          </router-link>
        </h1>
      </div>
    </div>

    <div
      v-if="showSearchModal"
      class="modal fade"
      :class="{
        in: showSearchModal
      }"
      id="searchModal"
      tabindex="-1"
      role="dialog"
      aria-labelledby="searchModalLabel"
      aria-hidden="true"
      :style="{
        display: showSearchModal ? 'block' : 'none',
        background: showSearchModal ? '#0005' : 'transparent'
      }"
    >
      <div class="modal-dialog">
        <div class="modal-content">
          <div class="modal-header">
            <button
              type="button"
              class="close"
              :class="{ 'is-disabled': updating }"
              @click="closeModal"
              aria-hidden="true"
            >
              &times;
            </button>
            <h4 class="modal-title" id="searchModalLabel">
              <i class="fa fa-fw fa-lg fa-search-plus text-primary"></i>
              {{ $label("customer.advanced_search", "Advanced search") }}
            </h4>
          </div>
          <div class="modal-body">
            <div class="row well">
              <div class="col-xs-12">
                <label class="input font-bold typo__label">{{
                  $label("booking.time_frames", "Time Frames")
                }}</label>
                <multiselect
                  v-model="filter.timeFrames"
                  tag-placeholder=""
                  placeholder="TimeFrame"
                  :options="eventTimeframes"
                  class="v-multiselect v-form"
                  :multiple="true"
                  :taggable="false"
                  :show-labels="false"
                  :close-on-select="true"
                >
                </multiselect>
              </div>
            </div>

            <div class="row">
              <div
                class="col-xs-6 col col-6 select-field"
                style="padding-left: 35px;"
              >
                <label class="input font-bold typo__label">{{
                  $label("customer.customer_type", "Customer Type")
                }}</label>
                <select class="form-control" v-model="filter.customer_type_id">
                  <option value="" selected>All</option>
                  <option
                    v-for="type in customer_typeOptions"
                    :value="type.id"
                    :key="type.id"
                    >{{ type.name }}</option
                  >
                </select>
              </div>

              <div class="col-xs-6 select-field" style="padding-right: 35px;">
                <label class="input font-bold typo__label">{{
                  $label("customer.group", "Customer Group")
                }}</label>
                <select class="form-control" v-model="filter.customer_group_id">
                  <option value="" selected>All</option>
                  <option
                    v-for="group in customer_groups"
                    :value="group.id"
                    :key="group.id"
                    >{{ group.name }}</option
                  >
                </select>
              </div>
            </div>

            <div class="row mb-8"><div class="col-xs-12">&nbsp;</div></div>

            <div class="row well">
              <div class="col-xs-6">
                <label class="input font-bold typo__label">{{
                  $label("booking.booking_status", "Booking Status")
                }}</label>
                <multiselect
                  v-model="filter.status"
                  tag-placeholder=""
                  placeholder="Add booking type"
                  :options="statuses"
                  class="v-multiselect v-form"
                  :multiple="true"
                  :taggable="false"
                  :show-labels="false"
                  :close-on-select="true"
                >
                </multiselect>
              </div>
              <div class="col-xs-6 col col-6 select-field">
                <label class="input font-bold typo__label">{{
                  $label("common.origin_booking", "Origin of The Booking")
                }}</label>
                <select class="form-control" v-model="filter.origin_booking_id">
                  <option value="" selected>All</option>
                </select>
              </div>
            </div>
          </div>
          <div class="modal-footer">
            <button
              type="button"
              :class="{ 'is-disabled': updating }"
              id="reset-filter"
              class="btn btn-default"
              @click="closeModal"
            >
              <i class="fa fa-fw fa-undo"></i>
              {{ $label("common.reset", "Reset") }}
            </button>
            <button
              type="button"
              :class="{ 'is-disabled': updating }"
              id="submit-filter"
              class="btn btn-primary"
              @click="handleSearch"
            >
              <i class="fa fa-fw fa-search"></i>
              {{ $label("common.confirm", "Confirm") }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <div class="row tooltip-wrapper popup-wrapper">
      <div class="col-xs-12">
        <alert-box
          v-show="failedRequest"
          type="warning"
          :closeable="false"
          :show-icon="false"
        >
          {{
            $label(
              "booking.events_failed_request",
              "Request failed while loading event bookings."
            )
          }}
        </alert-box>

        <Table
          v-show="!failedRequest"
          id="bookings-table"
          :loading="loading"
          :columns="[
            {
              title: $label('booking.event_name', 'Event Name'),
              id: row => `booking-name-${row.id}`,
              value: row => row.event.name
            },
            {
              title: $label('booking.customer', 'Booking'),
              id: row => `booking-customer-${row.id}`,
              value: row =>
                beautifyName(
                  row.booking.customer.first_name,
                  row.booking.customer.last_name
                ),
              onClick: row =>
                $router.push({
                  name: 'CustomerData',
                  params: { customer_id: row.booking.customer.id }
                })
            },
            {
              title: $label('booking.euro', 'EUR'),
              id: row => `booking-amount-${row.id}`,
              value: row => row.booking.total_price.amount_gross
            },
            {
              title: $label('booking.place', 'Place'),
              value: () => '-',
              id: row => `booking-place-${row.id}`
            },
            {
              title: $label('booking.status', 'Status'),
              value: row => row.booking.status,
              id: row => `booking-status-${row.id}`
            }
          ]"
          :actions="[
            {
              type: 'detail',
              onClick: row => {
                $router.push({
                  name: 'BookingEventDetails',
                  params: { booking_id: row.id }
                });
              }
            },
            {
              type: 'delete',
              icon: 'times',
              label: 'Cancel',
              bgColor: 'primary',
              onClick: row => cancelBooking(row)
            }
          ]"
          :data="bookings"
          :meta="metaData"
          selectable
          @onRowSelect="() => {}"
          @fetchQuery="
            params => {
              fetchParams = params;
              getBookings({
                //
              });
            }
          "
        >
          <div
            id="action-dropdown"
            slot="toolbar"
            class="btn-group"
            style="margin-left: 5px;"
          >
            <button
              type="button"
              class="btn btn-primary dropdown-toggle"
              data-toggle="dropdown"
            >
              <i class="fa fa-refresh"></i> &nbsp;
              {{ $label("common.actions", "Actions") }} &nbsp;<span
                class="caret"
              ></span>
            </button>
            <ul class="dropdown-menu" role="menu">
              <li>
                <router-link :to="{ name: 'NewBookingEvent' }"
                  ><i class="text-primary fa fa-fw fa-lg fa-plus"></i>
                  {{
                    $label("booking.create_new_booking", "Create new booking")
                  }}</router-link
                >
              </li>
            </ul>
          </div>

          <button
            slot="ext-search-button"
            class="btn btn-success booking-search-button"
            v-tooltip="{
              content: $label(
                'booking.more_search_filters',
                'More search filters'
              ),
              classes: 'custom-tooltip'
            }"
            type="button"
            data-toggle="modal"
            @click="openModal"
          >
            <i class="fa fa-fw fa-lg fa-search-plus"></i>
          </button>
        </Table>
      </div>
    </div>
  </div>
</template>

<script>
import Api from "@/api";
import Multiselect from "vue-multiselect";
import AlertBox from "@/components/Common/Alerts/AlertBox";
import Table from "@/components/Common/Table";

import swal from "sweetalert";
import "vue-slider-component/theme/default.css";

export default {
  name: "BookingEvents",

  components: {
    Table,
    Multiselect,
    AlertBox
  },

  data() {
    return {
      showSearchModal: false,
      componentKey: 0,
      bookings: [],
      loading: false,
      updating: false,
      failedRequest: false,
      metaData: null,
      fetchParams: {
        page: 1,
        per_page: null,
        search: ""
      },
      customer_typeOptions: [
        { name: "Privat", id: 1 },
        { name: "Firma", id: 2 },
        { name: "Verein", id: 3 },
        { name: "Kindergarten", id: 4 },
        { name: "Schule", id: 5 },
        { name: "Hobby", id: 6 }
      ],
      statuses: [],
      eventTimeframes: [],
      customer_groups: [],
      filter: {
        timeFrames: [],
        customer_type_id: null,
        customer_group_id: null,
        status: [],
        origin_booking_id: null
      },
      products: null
    };
  },

  beforeRouteEnter(to, from, next) {
    next(vm => {
      if (
        vm.$route.query.event_booking_deleted === true ||
        vm.$route.query.event_booking_deleted === "true"
      ) {
        swal("Success!", "The event booking was canceled.", "success").then(
          () => {
            window.history.pushState(
              {},
              document.title,
              window.location.pathname
            );
          }
        );
      }
      vm.getBookings(() => {
        //
      });
      vm.getGroups();
      vm.getBookingStatuses();
      vm.getEventTimeframes();
    });
  },

  beforeRouteUpdate(to, from, next) {
    if (to.query.event_booking_deleted === true) {
      swal("Success!", "The event booking was canceled.", "success").then(
        () => {
          window.history.pushState(
            {},
            document.title,
            window.location.pathname
          );
        }
      );
    }
    this.forceRerender();
    if (
      this.fetchParams.to % this.fetchParams.per_page === 0 &&
      this.fetchParams.page !== 1
    ) {
      this.fetchParams.page -= 1;
    }
    this.getBookings(() => {
      //
    });
    this.getGroups();
    this.getBookingStatuses();
    this.getEventTimeframes();
    next();
  },

  mounted() {
    //
  },

  methods: {
    openModal() {
      this.showSearchModal = true;
    },

    closeModal() {
      this.showSearchModal = false;
      this.filter = {
        eventFrames: [],
        customer_type_id: null,
        customer_group_id: null,
        status: this.statuses,
        origin_booking_id: null
      };
    },

    handleSearch() {
      this.getBookings();
      this.showSearchModal = false;
    },

    getBookings(successCallback) {
      this.loading = true;
      this.bookings = [];
      const endpoint = this.$partnerEndpoint("bookings/events");

      Api()
        .get(endpoint, {
          params: {
            page: this.fetchParams.page,
            search: this.fetchParams.search,
            per_page: this.fetchParams.per_page,
            ...this.filter
          }
        })
        .then(response => {
          this.bookings = response.data.data;
          this.metaData = response.data.meta;
          this.failedRequest = false;
          if (typeof successCallback == "function") {
            successCallback();
          }
          this.loading = false;
        })
        .catch(() => {
          this.failedRequest = true;
          this.loading = false;
        });
    },

    cancelBooking(eventBooking) {
      if (this.loading || this.updating) {
        return;
      }

      swal({
        title: "Are you sure?",
        text: "The event booking will be cancelled",
        icon: "warning",
        dangerMode: true,
        buttons: {
          cancel: {
            visible: true,
            text: "Cancel",
            closeModal: true
          },
          confirm: {
            visible: true,
            text: "Yes, delete it!",
            closeModal: true
          }
        }
      }).then(isConfirm => {
        if (isConfirm) {
          const endpoint = this.$partnerEndpoint(
            "bookings/" + eventBooking.booking.id
          );

          Api()
            .delete(endpoint)
            .then(() => {
              this.$router
                .push({
                  name: "BookingEvents",
                  query: { event_booking_deleted: true }
                })
                .catch(error => {
                  console.log(error);
                  if (
                    error.name === "NavigationDuplicated" ||
                    error.message.includes(
                      "Avoided redundant navigation to current location"
                    )
                  ) {
                    swal(
                      "Success!",
                      "The event booking was deleted.",
                      "success"
                    );
                    this.getBookings(() => {
                      //
                    });
                  }
                });
            })
            .catch(error => {
              if (error.response.status === 403) {
                swal({
                  title: "Missing permission",
                  text: "You are not allowed to delete external events.",
                  icon: "error"
                  //dangerMode: true,
                });
              }
            });
        } else {
          //
        }
      });
    },

    getGroups() {
      const endpoint = this.$partnerEndpoint("groups");

      Api()
        .get(endpoint)
        .then(response => {
          this.customer_groups = response.data.data;
        });
    },

    getBookingStatuses() {
      const endpoint = this.$partnerEndpoint("bookings/statuses");

      Api()
        .get(endpoint)
        .then(response => {
          this.statuses = response.data.data;
          this.filter.status = this.statuses;
        })
        .catch(error => {
          console.log(error);
        });
    },

    getEventTimeframes() {
      const endpoint = this.$partnerEndpoint("events/timeframes");

      Api()
        .get(endpoint)
        .then(response => {
          this.eventTimeframes = response.data.data;
        });
    },

    forceRerender() {
      this.componentKey += 1;
    }
  }
};
</script>

<style src="vue-multiselect/dist/vue-multiselect.min.css"></style>

<style scoped>
.mb-8 {
  margin-bottom: 1.3rem;
}

.booking-search-button {
  margin-bottom: 4px;
  padding: 5px 12px;
  margin-left: 5px;
}

.select-field {
  display: flex;
  flex-direction: column;
}

.select-field select {
  width: 16rem;
  height: 3.5rem;
  color: #000;
}
</style>
